import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { DataView } from "primereact/dataview";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toolbar } from "primereact/toolbar";
import { renderAsync as renderDocxAsync } from "docx-preview";
import { useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { IMAGE_TYPE_EXTENSIONS } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { IntakeDocument, IntakeSubmission, IntakeSubmissionsResponse } from "../../../models/intake";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { sanitizeRichHtml } from "../../../utils/securityUtils";
import IntakeFormSettingsModal from "../../home/components/IntakeFormSettingsModal";

const formatLosAngelesDateTime = (value?: string) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
};

const formatFileSize = (value?: number) => {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fullName = (person?: { firstName?: string; lastName?: string }) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ") || "Not provided";

const renderResidentConversionStatus = (submission: IntakeSubmission) => {
  if (submission.conversion?.status === "COMPLETED") {
    return <div className="mt-2"><Tag value="Resident created" severity="success" /></div>;
  }
  if (submission.conversion?.status === "TRANSFERRING_DOCUMENTS") {
    return <div className="mt-2"><span className="inline-flex align-items-center gap-2 text-sm text-orange-700"><ProgressSpinner style={{ width: "0.85rem", height: "0.85rem" }} strokeWidth="6" />Adding resident files…</span></div>;
  }
  return null;
};

const IntakeReceivedPage = () => {
  const { homeId = "" } = useParams();
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const isAdminUser = authStore?.isAdmin?.() === true;
  const docxPreviewContainerRef = useRef<HTMLDivElement>(null);
  const docxPreviewStyleContainerRef = useRef<HTMLDivElement>(null);
  const [response, setResponse] = useState<IntakeSubmissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewingDocumentId, setPreviewingDocumentId] = useState("");
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewNonImageUrl, setPreviewNonImageUrl] = useState("");
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [previewHtmlContent, setPreviewHtmlContent] = useState("");
  const [docxPreviewBlob, setDocxPreviewBlob] = useState<Blob | null>(null);
  const [loadingDocxPreview, setLoadingDocxPreview] = useState(false);
  const [previewRenderKey, setPreviewRenderKey] = useState(0);
  const [showIntakeSettings, setShowIntakeSettings] = useState(false);
  const [intakeFormEnabled, setIntakeFormEnabled] = useState<boolean | null>(null);
  const [publicScreeningToken, setPublicScreeningToken] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState<IntakeSubmission | null>(null);
  const [admittingSubmissionId, setAdmittingSubmissionId] = useState("");
  const [deletingSubmissionId, setDeletingSubmissionId] = useState("");
  const [admissionComplete, setAdmissionComplete] = useState<{ residentName: string; transferredDocumentCount: number } | null>(null);

  const loadSubmissions = useCallback(async () => {
    if (!homeId) return;
    setLoading(true);
    setErrorMessage("");
    try {
      await backendAPIStore.init();
      const [result, settings] = await Promise.all([
        backendAPIStore.getIntakeSubmissions(homeId),
        backendAPIStore.getIntakeSettings(homeId),
      ]);
      setResponse({
        home: result?.home || { homeId, businessName: "Facility" },
        items: Array.isArray(result?.items) ? result.items : [],
      });
      setIntakeFormEnabled(settings?.enabled === true);
      setPublicScreeningToken(settings?.enabled === true ? String(settings?.publicToken || "") : "");
    } catch (error: any) {
      setErrorMessage(error?.message || "Unable to load resident screenings.");
    } finally {
      setLoading(false);
    }
  }, [backendAPIStore, homeId]);

  useEffect(() => {
    void loadSubmissions();
  }, [loadSubmissions]);

  useEffect(() => {
    const openIntakeSettings = () => setShowIntakeSettings(true);
    window.addEventListener("intake-requests:manage-form", openIntakeSettings);
    return () => window.removeEventListener("intake-requests:manage-form", openIntakeSettings);
  }, []);

  const closeDocumentPreview = useCallback(() => {
    setShowDocumentPreview(false);
    setPreviewNonImageUrl("");
    setPreviewImageUrl("");
    setPreviewHtmlContent("");
    setDocxPreviewBlob(null);
    setLoadingDocxPreview(false);
    setPreviewTitle("");
  }, []);

  useEffect(() => {
    if (!docxPreviewBlob || !docxPreviewContainerRef.current) return;

    let cancelled = false;
    const bodyContainer = docxPreviewContainerRef.current;
    const styleContainer = docxPreviewStyleContainerRef.current;
    bodyContainer.innerHTML = "";
    if (styleContainer) styleContainer.innerHTML = "";
    setLoadingDocxPreview(true);

    void renderDocxAsync(docxPreviewBlob, bodyContainer, styleContainer || undefined, {
      inWrapper: true,
      hideWrapperOnPrint: false,
      ignoreWidth: false,
      ignoreHeight: false,
      ignoreFonts: false,
      breakPages: true,
      renderHeaders: true,
      renderFooters: true,
      renderFootnotes: true,
      renderEndnotes: true,
      ignoreLastRenderedPageBreak: false,
      useBase64URL: true,
      renderChanges: false,
      renderComments: true,
      renderAltChunks: true,
    })
      .catch((error: any) => {
        if (!cancelled) {
          closeDocumentPreview();
          setErrorMessage(error?.message || "Unable to render this Word document.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDocxPreview(false);
      });

    return () => {
      cancelled = true;
      bodyContainer.innerHTML = "";
      if (styleContainer) styleContainer.innerHTML = "";
    };
  }, [closeDocumentPreview, docxPreviewBlob]);

  const previewDocument = async (submission: IntakeSubmission, document: IntakeDocument) => {
    setPreviewingDocumentId(document.documentId);
    setErrorMessage("");
    setPreviewTitle(document.fileName || "Document Preview");
    setPreviewNonImageUrl("");
    setPreviewImageUrl("");
    setPreviewHtmlContent("");
    setDocxPreviewBlob(null);
    setLoadingDocxPreview(false);
    setPreviewRenderKey((current) => current + 1);
    setShowDocumentPreview(true);
    try {
      const result = await backendAPIStore.getIntakeDocumentPreview(homeId, submission.intakeSubmissionId, document.documentId);
      if (!result?.previewUrl) throw new Error("The document preview is unavailable.");

      const previewUrl = String(result.previewUrl);
      const fileExtension = document.fileName?.split(".").pop()?.toLowerCase();
      const mimeType = String(document.contentType || "").toLowerCase();
      if (fileExtension && IMAGE_TYPE_EXTENSIONS.includes(fileExtension)) {
        setPreviewImageUrl(previewUrl);
      } else if (fileExtension === "pdf" || mimeType.includes("application/pdf")) {
        setPreviewNonImageUrl(`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true&_=${Date.now()}`);
      } else if (fileExtension === "html" || fileExtension === "htm" || mimeType.includes("text/html")) {
        const htmlResponse = await fetch(previewUrl);
        if (!htmlResponse.ok) throw new Error("Unable to load the HTML document for preview.");
        setPreviewHtmlContent(sanitizeRichHtml(await htmlResponse.text()));
      } else if (fileExtension === "docx" || mimeType.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document")) {
        const docxResponse = await fetch(previewUrl);
        if (!docxResponse.ok) throw new Error("Unable to load the Word document for preview.");
        setDocxPreviewBlob(await docxResponse.blob());
      } else if (fileExtension === "doc" || mimeType.includes("application/msword")) {
        const docResponse = await fetch(previewUrl);
        if (!docResponse.ok) throw new Error("Unable to load the Word document for preview.");
        const docContent = await docResponse.text();
        if (/<html[\s>]/i.test(docContent) || /<!doctype html/i.test(docContent)) {
          setPreviewHtmlContent(sanitizeRichHtml(docContent));
        } else {
          setPreviewNonImageUrl(`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true&_=${Date.now()}`);
        }
      } else {
        setPreviewNonImageUrl(`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true&_=${Date.now()}`);
      }
    } catch (error: any) {
      closeDocumentPreview();
      setErrorMessage(error?.message || "Unable to preview the document.");
    } finally {
      setPreviewingDocumentId("");
    }
  };

  const renderDocuments = (submission: IntakeSubmission) => {
    if (submission.documentDelivery?.method === "fax") {
      return <Tag value="Fax confirmed" icon="pi pi-send" severity="warning" />;
    }
    if (!submission.documents?.length) return <span className="text-color-secondary">No documents</span>;
    return (
      <div className="flex flex-column gap-2">
        {submission.documents.map((document) => (
          <div key={document.documentId} className="flex align-items-center justify-content-between gap-2 flex-wrap">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="font-medium text-900" style={{ overflowWrap: "anywhere" }}>{document.fileName}</div>
              <small className="text-color-secondary">{formatFileSize(document.size)}</small>
            </div>
            <Button
              type="button"
              label="Preview"
              icon="pi pi-eye"
              size="small"
              severity="warning"
              outlined
              rounded
              loading={previewingDocumentId === document.documentId}
              onClick={() => void previewDocument(submission, document)}
            />
          </div>
        ))}
      </div>
    );
  };

  const addResidentFromSubmission = async (submission: IntakeSubmission) => {
    setErrorMessage("");
    setAdmittingSubmissionId(submission.intakeSubmissionId);
    try {
      await backendAPIStore.init();
      const result = await backendAPIStore.admitIntakeSubmission(submission.intakeSubmissionId);
      setSelectedSubmission(null);
      setAdmissionComplete({
        residentName: fullName(submission.residentDetails),
        transferredDocumentCount: Number(result?.transferredDocumentCount || 0),
      });
    } catch (error: any) {
      setErrorMessage(error?.message || "Unable to add the resident from this request.");
    } finally {
      setAdmittingSubmissionId("");
    }
  };

  const renderAddResidentButton = (submission: IntakeSubmission) => (
    submission.conversion?.status === "COMPLETED" ? null : (
      <Button
        type="button"
        label={admittingSubmissionId === submission.intakeSubmissionId ? "Adding Resident" : "Add Resident"}
        icon="pi pi-user-plus"
        severity="warning"
        rounded
        loading={admittingSubmissionId === submission.intakeSubmissionId}
        disabled={!!admittingSubmissionId || !!deletingSubmissionId}
        onClick={() => void addResidentFromSubmission(submission)}
        aria-label={`Add ${fullName(submission.residentDetails)} as a resident`}
      />
    )
  );

  const deleteAdmittedResident = async (submission: IntakeSubmission) => {
    setErrorMessage("");
    setDeletingSubmissionId(submission.intakeSubmissionId);
    try {
      await backendAPIStore.init();
      await backendAPIStore.resetIntakeSubmissionAdmission(submission.intakeSubmissionId);
      await loadSubmissions();
    } catch (error: any) {
      setErrorMessage(error?.message || "Unable to delete the resident for this request.");
    } finally {
      setDeletingSubmissionId("");
    }
  };

  const renderDeleteResidentButton = (submission: IntakeSubmission) => (
    isAdminUser && submission.conversion?.status === "COMPLETED" ? (
      <Button
        type="button"
        label={deletingSubmissionId === submission.intakeSubmissionId ? "Deleting Resident" : "Delete Resident"}
        icon="pi pi-trash"
        severity="danger"
        outlined
        rounded
        loading={deletingSubmissionId === submission.intakeSubmissionId}
        disabled={!!admittingSubmissionId || !!deletingSubmissionId}
        onClick={() => void deleteAdmittedResident(submission)}
        aria-label={`Delete ${fullName(submission.residentDetails)} and re-enable Add Resident`}
      />
    ) : null
  );

  const formatRequestFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      providerOneNumber: "Medicaid ID (ProviderOne)",
      ssnLastFour: "SSN (last 4 only)",
      attendingDoctorNpiNumber: "NPI number of attending doctor",
      nurseProgressNotesLastThreeDays: "Nurse progress notes from the last 3 days",
      fCodes: "F-codes",
      docCpsDrugCourt: "DOC, CPS, or Drug Court involvement",
    };
    return labels[field] || field.replace(/([A-Z])/g, " $1").replace(/^./, (value) => value.toUpperCase());
  };

  const renderReadOnlyFields = (data?: Record<string, any>) => (
    <div className="grid m-0 row-gap-1">
      {Object.entries(data || {}).map(([field, value]) => (
        <div className="col-12 md:col-6 px-2 py-1" key={field}>
          <div className="text-sm font-semibold mb-1" style={{ color: "#111827" }}>{formatRequestFieldLabel(field)}</div>
          {Array.isArray(value) ? (
            value.length ? <div className="flex flex-column gap-2">{value.map((entry, index) => typeof entry === "object" && entry !== null ? <div className="border-1 border-round p-2" style={{ borderColor: "#fdba74", background: "#fffaf5" }} key={`${field}-${index}`}><div className="font-semibold text-sm mb-2">Entry {index + 1}</div>{renderReadOnlyFields(entry)}</div> : <div key={`${field}-${index}`}>{String(entry || "Not provided")}</div>)}</div> : <div>Not provided</div>
          ) : value && typeof value === "object" ? renderReadOnlyFields(value) : <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{String(value || "Not provided")}</div>}
        </div>
      ))}
    </div>
  );

  const renderViewRequestButton = (submission: IntakeSubmission) => (
    <Button type="button" label="View Request" icon="pi pi-eye" severity="warning" outlined rounded onClick={() => setSelectedSubmission(submission)} />
  );

  const renderRequestSection = (title: string, icon: string, content: ReactNode) => (
    <section className="border-1 border-round overflow-hidden" style={{ borderColor: "#fdba74" }}>
      <div className="flex align-items-center gap-2 px-3 py-2" style={{ background: "#fff7ed" }}>
        <i className={icon} style={{ color: "#c2410c" }} />
        <div className="font-semibold">{title}</div>
      </div>
      <div className="p-2">{content}</div>
    </section>
  );

  const renderMobileCard = (submission: IntakeSubmission) => (
    <Card key={submission.intakeSubmissionId} className="mb-3" style={{ border: "1px solid #fdba74" }}>
      <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
        <div>
          <div className="text-xl font-semibold text-900">{fullName(submission.residentDetails)}</div>
          <div className="text-color-secondary mt-1">Submitted {formatLosAngelesDateTime(submission.submittedAt)}</div>
          {renderResidentConversionStatus(submission)}
        </div>
        <Tag
          value={submission.documentDelivery?.method === "fax" ? "Fax received" : "Submitted"}
          severity="warning"
        />
      </div>
      <div className="grid mt-3">
        <div className="col-12 sm:col-6"><div className="text-color-secondary text-sm">Referral contact</div><div className="font-medium">{fullName(submission.guestContact)}</div><div className="text-sm">{submission.guestContact?.emailAddress}</div></div>
        <div className="col-12 sm:col-6"><div className="text-color-secondary text-sm">Date of birth</div><div className="font-medium">{submission.residentDetails?.dateOfBirth || "Not provided"}</div></div>
        <div className="col-12 sm:col-6"><div className="text-color-secondary text-sm">Reference</div><div className="font-medium">{submission.intakeSubmissionId.slice(0, 8).toUpperCase()}</div></div>
      </div>
      <div className="flex justify-content-end gap-2 flex-wrap mt-4">{renderViewRequestButton(submission)}{renderAddResidentButton(submission)}{renderDeleteResidentButton(submission)}</div>
    </Card>
  );

  const items = response?.items || [];
  const homeName = response?.home?.businessName || "Facility";
  const publicScreeningUrl = publicScreeningToken
    ? `${window.location.origin}${RouterAddress.PUBLIC_INTAKE.replace(":token", encodeURIComponent(publicScreeningToken))}`
    : "";

  return (
    <div className="container-fluid p-3">
      <Toolbar
        start={renderSurfaceHeader("Resident Screening", `Review contact and referral details submitted to ${homeName}, preview supporting documents, and add approved applicants as residents.`, "pi pi-inbox")}
        end={publicScreeningUrl ? <Button type="button" label="Open Screening Form" icon="pi pi-external-link" iconPos="right" severity="warning" outlined onClick={() => window.open(publicScreeningUrl, "_blank", "noopener,noreferrer")} /> : null}
      />
      {errorMessage ? <Message severity="error" text={errorMessage} className="w-full mt-3" /> : null}
      {intakeFormEnabled === false ? <div className="flex justify-content-center mt-3"><Message severity="info" className="inline-flex" content={<div className="flex align-items-center gap-2 text-center"><i className="pi pi-cog" aria-hidden="true" /><div><strong>Screening form is turned off.</strong> Use the Manage Screening Form button above.</div></div>} /></div> : null}
      {loading && !response ? (
        <div className="flex justify-content-center p-6"><ProgressSpinner /></div>
      ) : !items.length ? (
        <Card className="mt-3 text-center">
          <i className="pi pi-inbox text-4xl text-orange-500" aria-hidden="true" />
          <div className="text-xl font-semibold mt-3">No resident screenings received</div>
          <div className="text-color-secondary mt-2">New submissions from the public request form will appear here.</div>
        </Card>
      ) : mobile ? (
        <DataView
          className="mt-3"
          value={items}
          itemTemplate={renderMobileCard}
          paginator={items.length > 10}
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
        />
      ) : (
        <Card className="mt-3">
          <DataTable value={items} dataKey="intakeSubmissionId" paginator={items.length > 10} rows={10} rowsPerPageOptions={[10, 25, 50]} stripedRows showGridlines responsiveLayout="scroll" emptyMessage="No resident screenings received.">
            <Column field="submittedAt" header="Submitted" sortable body={(item: IntakeSubmission) => formatLosAngelesDateTime(item.submittedAt)} style={{ minWidth: "11rem" }} />
            <Column header="Resident" sortable sortField="residentDetails.lastName" body={(item: IntakeSubmission) => <div><div className="font-semibold">{fullName(item.residentDetails)}</div><small className="text-color-secondary">DOB: {item.residentDetails?.dateOfBirth || "Not provided"}</small>{renderResidentConversionStatus(item)}</div>} style={{ minWidth: "12rem" }} />
            <Column header="Referral contact" body={(item: IntakeSubmission) => <div><div className="font-medium">{fullName(item.guestContact)}</div><div className="text-sm">{item.guestContact?.emailAddress}</div><small className="text-color-secondary">{item.guestContact?.phoneNumber || ""}</small></div>} style={{ minWidth: "14rem" }} />
            <Column header="Actions" body={(submission: IntakeSubmission) => <div className="flex gap-2 flex-wrap">{renderViewRequestButton(submission)}{renderAddResidentButton(submission)}{renderDeleteResidentButton(submission)}</div>} style={{ minWidth: "20rem" }} />
          </DataTable>
        </Card>
      )}
      <Dialog
        header={renderSurfaceHeader(previewTitle || "Document Preview", "Preview the selected request file.", "pi pi-eye")}
        visible={showDocumentPreview}
        modal
        onHide={closeDocumentPreview}
        style={{ ...appDialogStyle, width: "75vw", maxWidth: "1000px" }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
      >
        {(Boolean(previewingDocumentId) || loadingDocxPreview) && (
          <div className="flex justify-content-center p-4"><ProgressSpinner /></div>
        )}

        {!previewingDocumentId && !loadingDocxPreview && previewNonImageUrl && (
          <iframe key={`intake-preview-iframe-${previewRenderKey}`} title={previewTitle} src={previewNonImageUrl} style={{ width: "100%", height: "70vh", border: "1px solid #219d51" }} />
        )}

        {!previewingDocumentId && !loadingDocxPreview && previewHtmlContent && (
          <div
            key={`intake-preview-html-${previewRenderKey}`}
            style={{ width: "100%", height: "70vh", overflow: "auto", border: "1px solid #219d51", padding: "1rem", background: "#ffffff" }}
            dangerouslySetInnerHTML={{ __html: previewHtmlContent }}
          />
        )}

        {!previewingDocumentId && !loadingDocxPreview && previewImageUrl && (
          <div className="flex justify-content-center">
            <img key={`intake-preview-image-${previewRenderKey}`} src={previewImageUrl} alt={previewTitle} style={{ width: "100%", height: "70vh", objectFit: "contain", border: "1px solid #219d51" }} />
          </div>
        )}

        {!previewingDocumentId && docxPreviewBlob && (
          <div style={{ width: "100%", height: "70vh", overflow: "auto", border: "1px solid #219d51", padding: "1rem", background: "#eef2f7" }}>
            <div ref={docxPreviewStyleContainerRef} aria-hidden="true" style={{ display: "none" }} />
            <div ref={docxPreviewContainerRef} key={`intake-preview-docx-${previewRenderKey}`} />
          </div>
        )}

        <div className="mt-4 flex justify-content-end">
          <Button type="button" label="Close" outlined onClick={closeDocumentPreview} />
        </div>
      </Dialog>
      <Dialog
        header={renderSurfaceHeader("Resident Screening", selectedSubmission ? `${fullName(selectedSubmission.residentDetails)} · submitted ${formatLosAngelesDateTime(selectedSubmission.submittedAt)}` : "", "pi pi-file-search")}
        visible={!!selectedSubmission}
        modal
        onHide={() => setSelectedSubmission(null)}
        style={{ ...appDialogStyle, width: "min(94vw, 72rem)" }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={{ ...appDialogContentStyle, maxHeight: "calc(100vh - 9rem)", overflowY: "auto" }}
      >
        {selectedSubmission ? <div className="flex flex-column gap-2">
          <div className="border-1 border-round p-2 flex justify-content-between align-items-start gap-3 flex-wrap" style={{ borderColor: "#fdba74", background: "#fffaf5" }}>
            <div><div className="text-sm text-color-secondary mb-1">Resident screening</div><div className="text-2xl font-semibold">{fullName(selectedSubmission.residentDetails)}</div><div className="text-color-secondary mt-1">Submitted {formatLosAngelesDateTime(selectedSubmission.submittedAt)}</div></div>
            <Tag value={selectedSubmission.documentDelivery?.method === "fax" ? "Fax confirmed" : "Submitted"} severity="warning" />
          </div>
          {renderRequestSection("Referral contact", "pi pi-user", renderReadOnlyFields(selectedSubmission.guestContact))}
          {renderRequestSection("Referral information", "pi pi-send", renderReadOnlyFields(selectedSubmission.referralInformation))}
          {renderRequestSection("Resident details", "pi pi-home", renderReadOnlyFields(selectedSubmission.residentDetails))}
          {renderRequestSection("Clinical screening", "pi pi-heart", renderReadOnlyFields(selectedSubmission.clinicalScreening as Record<string, any>))}
          {renderRequestSection("Document delivery", "pi pi-file", renderReadOnlyFields(selectedSubmission.documentDelivery as Record<string, any>))}
          {renderRequestSection("Supporting documents", "pi pi-folder", renderDocuments(selectedSubmission))}
        </div> : null}
      </Dialog>
      <Dialog
        header={renderSurfaceHeader("Resident created", "The resident screening was added successfully.", "pi pi-check-circle")}
        visible={!!admissionComplete}
        modal
        closable={false}
        onHide={() => undefined}
        style={{ ...appDialogStyle, width: "min(92vw, 34rem)" }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        footer={
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              label="Close"
              outlined
              onClick={() => { setAdmissionComplete(null); void loadSubmissions(); }}
            />
            <Button
              type="button"
              label="Redirect to Dashboard"
              icon="pi pi-home"
              severity="warning"
              onClick={() => navigate(RouterAddress.PROVIDER_HOME)}
            />
          </div>
        }
      >
        {admissionComplete ? (
          <div className="line-height-3 text-900">
            <p className="mt-0 mb-3"><strong>{admissionComplete.residentName}</strong> has been created as a resident.</p>
            <div className="border-1 border-round p-3" style={{ borderColor: "#fdba74", background: "#fff7ed" }}>
              <div className="font-semibold mb-1"><i className="pi pi-folder mr-2" style={{ color: "#c2410c" }} />Referral Forms</div>
              <div>{admissionComplete.transferredDocumentCount
                ? `${admissionComplete.transferredDocumentCount} attached file${admissionComplete.transferredDocumentCount === 1 ? " has" : "s have"} been added to this folder in the resident Binder.`
                : "No uploaded files were included with this request. The Referral Forms folder is ready in the resident Binder."}</div>
            </div>
          </div>
        ) : null}
      </Dialog>
      <IntakeFormSettingsModal
        visible={showIntakeSettings}
        onHide={() => { setShowIntakeSettings(false); void loadSubmissions(); }}
        homeId={homeId}
        homeName={homeName}
      />
    </div>
  );
};

export default IntakeReceivedPage;
